import { Col, Container, Row } from 'react-bootstrap'
import styles from './VpnLogs.module.css'
import { gql } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import { useEffect, useMemo, useState } from 'react'
import Log, { VPN_LOGIN_STATUS, VpnLoginType } from '../log/Log'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useModifyPage, useModifyRecordsPerPage, usePaginationData, useSetSearchUrlParams } from '../../../../../hooks/usePagination'
import SearchContainer from './search-container/SearchContainer'
import ErrorMessage from '../../../../../components/error-message/ErrorMessage'
import Pagination from '../../../../../components/pagination/Pagination'
import LoadingSkeleton from '../../../../../components/loading-skeleton/LoadingSkeleton'

const VPN_LOGINS_QUERY = gql`
    query VpnLogins($searchParameters: VpnLoginSearchParameters!, $orderBy: LoginUserSortField, $direction: SortDirection, $pagination: PaginationInput!) {
        vpnLogins(searchParameters: $searchParameters, orderBy: $orderBy, direction: $direction, pagination: $pagination) {
            maxPages
            currentPage
            recordsPerPage
            vpnLogins {
                id
                ipAddress
                status
                generatedToken
                loginAt
                logoutAt
                user {
                    username
                }
            }
            success
            errorMessage
        }
    }
`

interface vpnLoginsQueryResult {
    vpnLogins: {
        maxPages: number
        currentPage: number
        recordsPerPage: number
        success: boolean,
        errorMessage: string,
        vpnLogins: VpnLoginType[]
    }
}

enum LOGIN_SORT_DIRECTION {
    ASC = "asc",
    DESC = "desc"
}

enum LOGIN_SORT_FIELD {
    ID = "id",
    IP = "ip",
    USERNAME = "username",
    LOGIN = "login",
    LOGOUT = "logout",
    STATUS = "status"
}

export type VpnLoginSearchParams = {
    user: {
        username: string | null
    }
    ipAddress: string | null
    loginAt: string | null
    logoutAt: string | null
    status: VPN_LOGIN_STATUS | null
}

export type SortSearchParamsType = {
    field: LOGIN_SORT_FIELD | null
    direction: LOGIN_SORT_DIRECTION | null
}

export default function VpnLogs() {
    const client = useApolloClient()
    const [ vpnLogins, setVpnLogins ] = useState<VpnLoginType[]>([])
    const [ searchParams, setSearchParams ] = useSearchParams()
    const [ maxPages, setMaxPages ] = useState(-1)
    const [ loading, setLoading ] = useState(true)
    const pagination = usePaginationData(searchParams, maxPages)
    const modifyPage = useModifyPage(setSearchParams, pagination)
    const modifyRecordsPerPage = useModifyRecordsPerPage(setSearchParams)
    const [ showError, setShowError ] = useState(false)
    const [ errorMessage, setErrorMessage ] = useState("")
    const setSearchUrlParams = useSetSearchUrlParams(setSearchParams)
    const [ defaultLoginAtDateFilter, setDefaultLoginAtDateFilter ] = useState<Date|null>(new Date())
    const location = useLocation()

    const searchParameters = useMemo<VpnLoginSearchParams>(() => {
        let user: { username: string | null } = { username: searchParams.get('user')}
        let loginAt: string | null = searchParams.get('loginat')
        let logoutAt: string | null = searchParams.get('logoutat')
        let ipAddress: string | null = searchParams.get('ipaddress')
        let status = searchParams.get('status') as (VPN_LOGIN_STATUS | null)

        if(status !== null) {
            status = status.toUpperCase() as VPN_LOGIN_STATUS
            if(!Object.values(VPN_LOGIN_STATUS).includes(status)) {
                status = null
            }
        }

        if(loginAt === null && defaultLoginAtDateFilter !== null) {
            loginAt = defaultLoginAtDateFilter.toISOString().split('T')[0]
        }

        const ret: VpnLoginSearchParams = {
            user,
            ipAddress,
            loginAt,
            logoutAt,
            status
        }
        return ret
    }, [ searchParams, defaultLoginAtDateFilter ])

    const sortBy = useMemo<SortSearchParamsType>(() => {
        let orderBy = searchParams.get('orderby') as (LOGIN_SORT_FIELD | null)
        let direction = searchParams.get('direction') as (LOGIN_SORT_DIRECTION | null)

        if(orderBy !== null) {
            orderBy = orderBy.toLowerCase() as LOGIN_SORT_FIELD
            if(!Object.values(LOGIN_SORT_FIELD).includes(orderBy)) {
                orderBy = null
            }
        }

        if(orderBy === null) {
            direction = null
        }
        else {
            if(direction !== null) {
                direction = direction.toLowerCase() as LOGIN_SORT_DIRECTION
                if(!Object.values(LOGIN_SORT_DIRECTION).includes(direction)) {
                    direction = null
                }
            }
        }

        return {
            field: orderBy,
            direction
        }
    }, [ searchParams ])

    useEffect(() => {
        const getVpnLogins = async () => {
            setLoading(true)
            
            const data = await client.query<vpnLoginsQueryResult>({
                query: VPN_LOGINS_QUERY,
                variables: {
                    searchParameters,
                    orderBy: sortBy.field ? sortBy.field.toUpperCase() : undefined,
                    direction: sortBy.direction ? sortBy.direction.toUpperCase() : undefined,
                    pagination
                },
                fetchPolicy: 'no-cache'
            })
           
            const { maxPages, recordsPerPage, currentPage, vpnLogins, success, errorMessage } = data.data!.vpnLogins

            setMaxPages(maxPages)

            if(pagination.recordsPerPage !== recordsPerPage) {
                modifyRecordsPerPage(recordsPerPage)
            }

            if(pagination.currentPage !== currentPage) {
                modifyPage(currentPage)
            }

            setShowError(!success || !vpnLogins || vpnLogins.length === 0)
            setErrorMessage(errorMessage)

            if(vpnLogins) {
                setVpnLogins(vpnLogins)
            }
            setLoading(false)

        }
        
        getVpnLogins()
    },[ client, sortBy, pagination, modifyPage, modifyRecordsPerPage, searchParameters ])

    useEffect(() => {
        if(location.search.length === 0) {
            setDefaultLoginAtDateFilter(new Date())
        }
    }, [ location ])

    const toggleSort = (field: LOGIN_SORT_FIELD) => {
        if(loading) {
            return
        }

        let newDirection = LOGIN_SORT_DIRECTION.ASC
        if(sortBy.field === field) {
            newDirection = sortBy.direction === LOGIN_SORT_DIRECTION.ASC ? LOGIN_SORT_DIRECTION.DESC : LOGIN_SORT_DIRECTION.ASC
        }

        setSearchUrlParams({
            orderby: field,
            direction: newDirection
        })

        setLoading(true)
    }

    const getArrowSrc = (field: LOGIN_SORT_FIELD) => {
        if (sortBy.field !== field) {
            return "/updown-arrow.png"
        }
        return sortBy.direction === "asc" ? "/uparrow.png" : "/downarrow.png"
    }

    return(
        <>
            <SearchContainer
                searchParameters={ searchParameters }
                setSearchParams={ setSearchParams }
                removeDefaultLoginAtDateFilter={() => setDefaultLoginAtDateFilter(null)}
                pagination={
                    <Pagination
                        maxPages={ maxPages }
                        currentPage={ pagination.currentPage }
                        recordsPerPageProps="Log / Oldal"
                        recordsPerPage={ pagination.recordsPerPage }
                        modifyPage={page => {
                            modifyPage(page)
                            setLoading(true)
                        }}
                        modifyRecordsPerPage={rpp => {
                            modifyRecordsPerPage(rpp)
                            setLoading(true)
                        }}
                    />
                }
            />
                       
            <Container className={ styles.container }>
                <Row className={styles.containerHead}>
                    <Col className={styles.disableColPadding} sm="1">ID
                        <button onClick={() => toggleSort(LOGIN_SORT_FIELD.ID)} className={styles.colButtons}>
                            <img id='id' className={styles.colButtonsImage} src={getArrowSrc(LOGIN_SORT_FIELD.ID)} alt="sorting" />
                        </button>
                    </Col>

                    <Col className={styles.disableColPadding} sm="2">Felhasználónév
                        <button onClick={() => toggleSort(LOGIN_SORT_FIELD.USERNAME)} className={styles.colButtons}>
                            <img id='username' className={styles.colButtonsImage} src={getArrowSrc(LOGIN_SORT_FIELD.USERNAME)} alt="sorting" />
                        </button>
                    </Col>

                    <Col className={styles.disableColPadding} sm="1">IP
                       <button onClick={() => toggleSort(LOGIN_SORT_FIELD.IP)} className={styles.colButtons}>
                            <img id='username' className={styles.colButtonsImage} src={getArrowSrc(LOGIN_SORT_FIELD.IP)} alt="sorting" />
                        </button>
                    </Col>

                    <Col className={styles.disableColPadding} sm="2">Belépés
                        <button onClick={() => toggleSort(LOGIN_SORT_FIELD.LOGIN)} className={styles.colButtons}>
                            <img id='login' className={styles.colButtonsImage} src={getArrowSrc(LOGIN_SORT_FIELD.LOGIN)} alt="sorting" />
                        </button>
                    </Col>

                    <Col className={styles.disableColPadding} sm="2">Kilépés
                        <button onClick={() => toggleSort(LOGIN_SORT_FIELD.LOGOUT)} className={styles.colButtons}>
                            <img id='logout' className={styles.colButtonsImage} src={getArrowSrc(LOGIN_SORT_FIELD.LOGOUT)} alt="sorting" />
                        </button>
                    </Col>

                    <Col className={styles.disableColPadding} sm="1">Időtartam</Col>

                    <Col className={styles.disableColPadding} sm="1">Token</Col>

                    <Col className={styles.disableColPadding} sm="2">Státusz
                       <button onClick={() => toggleSort(LOGIN_SORT_FIELD.STATUS)} className={styles.colButtons}>
                            <img id='login' className={styles.colButtonsImage} src={getArrowSrc(LOGIN_SORT_FIELD.STATUS)} alt="sorting" />
                        </button>
                    </Col>
                </Row>
                    {
                        loading
                        ?
                            <LoadingSkeleton rows={pagination.recordsPerPage} cells={4} />
                        : showError
                            ? <ErrorMessage message={errorMessage}/>
                            :
                            vpnLogins.map((login, index) => <Log key={`vpn-login-id-${login.id}`} log={login} odd={ index % 2 === 1 } />)
                    }
            </Container>

            <Container className={ styles.lowerPaginationContainer }>
                <Row className="g-0 position-relative">
                    <Col className='col-4'/>
                    <Col className='col-8 d-flex relative align-items-center flex-row-reverse'>
                        <Pagination
                            maxPages={ maxPages }
                            currentPage={ pagination.currentPage }
                            recordsPerPageProps="Log / Oldal"
                            recordsPerPage={ pagination.recordsPerPage }
                            modifyPage={page => {
                                modifyPage(page)
                            }}
                            modifyRecordsPerPage={rpp => {
                                modifyRecordsPerPage(rpp)
                            }}
                        />
                    </Col>
                </Row>
            </Container>
        </>
    )
}